import type Account from '@/data/account/model';
import type Plan from '@/data/plan/model';
import type User from '@/data/user/model';
import type { StringOrMoment } from '@/data/user/model';

import moment from 'moment-timezone';

const VITALLY_SCRIPT_ID = 'vitally-nps-script';

interface VitallyApi {
  init: (...args: unknown[]) => void;
  user: (...args: unknown[]) => void;
  account: (...args: unknown[]) => void;
  organization: (...args: unknown[]) => void;
  track: (...args: unknown[]) => void;
  nps: (...args: unknown[]) => void;
}

type VitallyQueue = unknown[][] & VitallyApi;

declare global {
  interface Window {
    Vitally?: VitallyApi;
  }
}

function formatCreatedAt(value: StringOrMoment | null): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = moment(value);
  return date.isValid() ? date.toISOString() : undefined;
}

function installVitallyQueue(): VitallyApi {
  if (window.Vitally) {
    return window.Vitally;
  }

  const queue = [] as unknown as VitallyQueue;
  for (const method of ['init', 'user', 'account', 'organization', 'track', 'nps'] as const) {
    queue[method] = (...args: unknown[]) => {
      queue.push([method, args]);
    };
  }
  window.Vitally = queue;
  return queue;
}

function loadVitallyScript(): void {
  if (document.getElementById(VITALLY_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = VITALLY_SCRIPT_ID;
  script.src = CONFIG.VENDORS.VITALLY_NPS.SCRIPT_URL;
  script.defer = true;
  document.head.appendChild(script);
}

export function identifyVitallyNps(user: User, account: Account, plan: Plan): void {
  const config = CONFIG.VENDORS.VITALLY_NPS;
  if (!config.ENABLED) {
    return;
  }

  const vitally = installVitallyQueue();
  loadVitallyScript();
  vitally.init(config.TOKEN, config.API_HOST);
  vitally.account({
    accountId: String(account.id),
    traits: {
      name: account.company,
      planId: plan.id,
      planName: plan.name,
      createdAt: formatCreatedAt(account.created_at),
    },
  });
  vitally.user({
    userId: String(user.id),
    accountId: String(account.id),
    traits: {
      name: user.fullName,
      email: user.email,
      role: user.roleName,
      createdAt: formatCreatedAt(user.created_at),
    },
  });
  vitally.nps('survey', {
    productName: config.PRODUCT_NAME,
    primaryColor: config.PRIMARY_COLOR,
    npsQuestion: config.QUESTION,
  });
}
